import React, { useMemo, useState } from 'react';
import { useQueries, useQuery } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import {
  getCmiDigestAdminUserPreference,
  getCmiDigestAdminUsersCommunities,
  updateCmiDigestAdminUsersBulk,
} from 'apis/UserAPI';
import { useRootContext } from 'components/layout/RootContext';
import Loader from 'components/lib/Loader';
import Radio from 'components/lib/Radio';
import TextArea from 'components/lib/TextArea';

import { CMI_DIGEST_FREQUENCY_OPTIONS } from '../../../constants';
import {
  AiInstructionsCard,
  AiInstructionsDescription,
  AiInstructionsHeader,
  AiInstructionsLabel,
  AiTag,
  Body,
  Card,
  FrequencyLabel,
  FrequencyRow,
  MainColumn,
  SectionTitle,
  StyledCheckbox,
  Switcher,
} from '../CmiDigestForm';
import { CmiDigestAdminUser, CmiDigestUserCommunities } from '../types';
import { showCmiDigestSelectionRequiredModal } from '../validation';
import CmiDigestBulkCommunitiesPicker from './CmiDigestBulkCommunitiesPicker';
import CmiDigestModalWrapper from './CmiDigestModalWrapper';

const EnableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 30px;
`;

const EnableLabel = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

type Props = {
  users: CmiDigestAdminUser[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const CmiDigestBulkUserModal = ({ users, isOpen, onClose, onSaved }: Props) => {
  const { showMessage } = useRootContext();
  const userIds = useMemo(() => users.map((user) => user.id), [users]);

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [communitiesByUser, setCommunitiesByUser] = useState<Record<number, number[]>>({});
  const [touchedUserIds, setTouchedUserIds] = useState<Set<number>>(new Set());
  const isEnabled: boolean = Form.useWatch('enabled', form) ?? true;

  const { data: usersCommunities = [], isLoading: isLoadingOptions } = useQuery<CmiDigestUserCommunities[]>(
    ['cmiDigestAdminUsersCommunities', userIds],
    () => getCmiDigestAdminUsersCommunities(userIds)
  );

  const preferenceQueries = useQueries(
    users.map((user) => ({
      queryKey: ['cmiDigestAdminUserPreference', user.id],
      queryFn: () => getCmiDigestAdminUserPreference(user.id),
    }))
  );
  const isLoadingBaseline = preferenceQueries.some((query) => query.isLoading);
  const isLoading = isLoadingOptions || isLoadingBaseline;

  const baselinePreference = (userId: number) => {
    const index = users.findIndex((user) => user.id === userId);
    return preferenceQueries[index]?.data;
  };

  const baselineCommunityIds = (userId: number) => baselinePreference(userId)?.communities ?? [];

  const handleEnabledChange = (e: any) => {
    if (e.target.value !== false) return;

    form.setFields([
      { name: 'competitor_incentives_enabled', value: false, touched: true },
      { name: 'community_reviews_enabled', value: false, touched: true },
    ]);
    setTouchedUserIds((prev) => new Set([...prev, ...userIds]));
    setCommunitiesByUser((prev) => {
      const next = { ...prev };
      userIds.forEach((userId) => {
        next[userId] = [];
      });
      return next;
    });
  };

  const handleSave = async () => {
    const values = form.getFieldsValue();
    const enabledTouched = form.isFieldTouched('enabled');
    const incentivesTouched = form.isFieldTouched('competitor_incentives_enabled');
    const reviewsTouched = form.isFieldTouched('community_reviews_enabled');

    // "Enable" left untouched doesn't mean "not enabled" - it means each user keeps whatever
    // enabled state they already had, and a bulk edit that only clears communities (say) can still
    // leave an already-enabled user with a dead digest. Validate against each user's own resulting
    // state, not the raw (possibly untouched/undefined) form values - see PA-3106.
    const effectivelyEnabledUsers = users.filter((user) =>
      enabledTouched ? values.enabled : baselinePreference(user.id)?.enabled
    );

    if (effectivelyEnabledUsers.length > 0) {
      const missingFrequency = enabledTouched && values.enabled && !values.frequency;
      const missingContent = effectivelyEnabledUsers.some((user) => {
        const baseline = baselinePreference(user.id);
        const incentives = incentivesTouched
          ? values.competitor_incentives_enabled
          : baseline?.competitor_incentives_enabled;
        const reviews = reviewsTouched ? values.community_reviews_enabled : baseline?.community_reviews_enabled;
        return !incentives && !reviews;
      });
      const missingCommunity = effectivelyEnabledUsers.some(
        (user) =>
          (touchedUserIds.has(user.id) ? (communitiesByUser[user.id] ?? []) : baselineCommunityIds(user.id)).length ===
          0
      );

      if (missingFrequency || missingContent || missingCommunity) {
        showCmiDigestSelectionRequiredModal({ includeFrequency: true });
        return;
      }
    }

    setSaving(true);
    try {
      const payload: any = { user_ids: userIds };
      if (form.isFieldTouched('enabled')) payload.enabled = values.enabled;
      if (form.isFieldTouched('frequency')) payload.frequency = values.frequency;
      if (form.isFieldTouched('competitor_incentives_enabled')) {
        payload.competitor_incentives_enabled = values.competitor_incentives_enabled;
      }
      if (form.isFieldTouched('community_reviews_enabled')) {
        payload.community_reviews_enabled = values.community_reviews_enabled;
      }
      if (form.isFieldTouched('ai_instructions')) payload.ai_instructions = values.ai_instructions;

      if (touchedUserIds.size > 0) {
        payload.communities_by_user = Object.fromEntries(
          Array.from(touchedUserIds).map((userId) => [userId, communitiesByUser[userId] ?? []])
        );
      }

      await updateCmiDigestAdminUsersBulk(payload);
      showMessage('success', 'CMI Digest settings updated successfully');
      onSaved();
    } catch (error) {
      showMessage('error', 'Failed to update CMI Digest settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CmiDigestModalWrapper
      title="CMI Digest"
      subtitle={`${users.length} Users selected`}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      disabled={saving || isLoading}
      communitiesToModify={[]}
      width={1150}
      saveButtonTestId="cmi-digest-bulk-modal-save-btn"
      cancelButtonTestId="cmi-digest-bulk-modal-cancel-btn"
    >
      {isLoading ? (
        <LoaderWrapper>
          <Loader active />
        </LoaderWrapper>
      ) : (
        <Form form={form} component={false}>
          <Body>
            <MainColumn className="no-border mini-scroll">
              <Switcher className="no-border">
                <EnableRow>
                  <EnableLabel>Enable CMI Digest</EnableLabel>
                  <Form.Item name="enabled" noStyle>
                    <Radio.Group onChange={handleEnabledChange}>
                      <Radio value={true} data-test-id="cmi-digest-bulk-enable-on">
                        On
                      </Radio>
                      <Radio value={false} data-test-id="cmi-digest-bulk-enable-off">
                        Off
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </EnableRow>
                <FrequencyRow>
                  <FrequencyLabel>Frequency</FrequencyLabel>
                  <Form.Item name="frequency" noStyle>
                    <Radio.Group disabled={!isEnabled}>
                      {CMI_DIGEST_FREQUENCY_OPTIONS.map((option) => (
                        <Radio
                          key={option.value}
                          value={option.value}
                          data-test-id={`cmi-digest-bulk-frequency-${option.value}`}
                        >
                          {option.label}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </FrequencyRow>
              </Switcher>
              <SectionTitle>Content Included</SectionTitle>
              <Card className="no-border">
                <Form.Item name="competitor_incentives_enabled" valuePropName="checked" initialValue={false} noStyle>
                  <StyledCheckbox
                    className="green"
                    disabled={!isEnabled}
                    data-test-id="cmi-digest-bulk-content-competitor-incentives"
                  >
                    Competitor Incentives
                  </StyledCheckbox>
                </Form.Item>
              </Card>
              <Card className="no-border">
                <Form.Item name="community_reviews_enabled" valuePropName="checked" initialValue={false} noStyle>
                  <StyledCheckbox
                    className="green"
                    disabled={!isEnabled}
                    data-test-id="cmi-digest-bulk-content-community-reviews"
                  >
                    My Community Reviews
                  </StyledCheckbox>
                </Form.Item>
              </Card>
              <AiInstructionsCard className="no-border">
                <AiInstructionsHeader>
                  <AiInstructionsLabel>AI Instructions</AiInstructionsLabel>
                  <AiTag>AI</AiTag>
                </AiInstructionsHeader>
                <AiInstructionsDescription>
                  Give instructions of an AI agent on what to prioritize or additionally tweak your needs.
                </AiInstructionsDescription>
                <Form.Item name="ai_instructions" initialValue="" noStyle>
                  <TextArea
                    rows={4}
                    disabled={!isEnabled}
                    placeholder="e.g. Negative reviews about care or staffing matter most to us, or flag incentives like a waived community fee or more than one month free — not just any promotion."
                    data-test-id="cmi-digest-bulk-ai-instructions-textarea"
                  />
                </Form.Item>
              </AiInstructionsCard>
            </MainColumn>

            <CmiDigestBulkCommunitiesPicker
              usersCommunities={usersCommunities}
              baselineCommunityIds={baselineCommunityIds}
              communitiesByUser={communitiesByUser}
              setCommunitiesByUser={setCommunitiesByUser}
              touchedUserIds={touchedUserIds}
              setTouchedUserIds={setTouchedUserIds}
              disabled={!isEnabled}
            />
          </Body>
        </Form>
      )}
    </CmiDigestModalWrapper>
  );
};

export default CmiDigestBulkUserModal;
